// KYD distance fields ("distance d'alerte", etc.) need to show/accept the decimal separator
// that matches the site's own language, not whatever the browser's own locale happens to be -
// a native `type="number"` input's separator follows the latter, which can silently reject (or
// misread) the character the French/English UI actually leads the user to expect. Kept here
// rather than duplicated across tracker-config-form.component.ts and any other place editing
// the same kind of value.
export function decimalSeparator(lang: string): string {
  return lang === 'fr-FR' ? ',' : '.';
}

// Formats an already-valid meters value for display, locale-correct separator - a whole
// number ("5") stays a whole number, with no forced ".00"/",00": decimals only show up when
// the value actually has them (typically because the user typed some). Rounded to 2 decimal
// places first to avoid floating-point artifacts (e.g. 0.1 + 0.2) leaking into the display.
// Used both for input fields (on blur) and for read-only distance displays (kyd.component.html,
// edit-tracker-modal.component.html), so a saved value reads the same way everywhere on site.
export function formatDistanceMeters(value: number | null | undefined, lang: string): string {
  if (value == null || isNaN(value)) return '';
  const rounded = Math.round(value * 100) / 100;
  return rounded.toString().replace('.', decimalSeparator(lang));
}

// Corrects the separator live as the user types (e.g. "." typed where "," is expected in
// French) and drops anything else that isn't a digit or that separator - a distance is never
// negative, so a minus sign is dropped too rather than typed then rejected on blur.
export function sanitizeDistanceText(raw: string, lang: string): string {
  if (!raw) return '';
  const wanted = decimalSeparator(lang);
  const other = wanted === ',' ? '.' : ',';
  const swapped = raw.split(other).join(wanted);
  const digitsAndSep = swapped.split('').filter(ch => (ch >= '0' && ch <= '9') || ch === wanted).join('');

  const firstSep = digitsAndSep.indexOf(wanted);
  if (firstSep === -1) return digitsAndSep;
  return digitsAndSep.slice(0, firstSep + 1) + digitsAndSep.slice(firstSep + 1).split(wanted).join('');
}

// Accepts either separator regardless of language - sanitizeDistanceText already normalized
// anything actually typed through a field, but a value coming from elsewhere shouldn't have to
// go through that first.
export function parseDistanceText(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim().replace(',', '.');
  if (!trimmed) return null;
  const value = parseFloat(trimmed);
  return isNaN(value) ? null : value;
}
